import { Component, EventEmitter, Input, Output, ViewContainerRef, ViewEncapsulation } from '@angular/core';
import { Subscription } from 'rxjs';

import { util } from '../../classes/util';
import { BaseComponent } from '../base.component';

@Component({
    encapsulation: ViewEncapsulation.ShadowDom,
    selector: 'mc-scope',
    styleUrls: ['../sharedStyles/base.scss', './scope.component.scss'],
    templateUrl: './scope.component.html'
})
export class ScopeComponent extends BaseComponent {
    get includeGoogleAnalytics() { return false; }
    get includeEsriCss() { return false; }
    get includeOverrideCss() { return false; }

    constructor(
        vcr: ViewContainerRef
    ) {
        super(vcr);
        this.registerEvent('onregister', this.register);
        this.registerEvent('onunregister', this.unregister);
    }

    @Output()
    public readonly register = new EventEmitter<IRegistrationEvent>();

    @Output()
    public readonly unregister = new EventEmitter<IRegistrationEvent>();

    private readonly componentsByTag = {} as { [tagName: string]: BaseComponent[] };

    /**
     * In order to detect timing issues, this keeps track of when a request for a component
     * returns a default value that was not explicitly requested (either by ID or through the defaults object).
     *
     * The first time a default is returned, this flag gets set to true for that tag. If new elements are registered after
     * a non-explicitly-requested default is returned, an exception will be thrown.
     */
    private readonly defaultWasReturned = {} as { [tagName: string]: true | undefined };

    /**
     * Register a child with this scope for other siblings to access.
     * This should only be called by child mc-map components.
     * You can pass any component into this function will no ill-effects;
     * this function will only actually "register" the component types it is looking for.
     */
    @Input() // use @input to make this method show up on the actual HTML element
    public registerComponent = (component: BaseComponent) => {
        const tagName = component.hostElement.tagName.toLowerCase();
        let container = this.componentsByTag[tagName];
        if (!container) {
            container = this.componentsByTag[tagName] = [];
        }

        // if the component is already registered, log a warning and exit (everything will still work, but developers should fix it)
        if (container.indexOf(component) > -1) {
            console.warn('Component is already registered with scope', component, this);
            return;
        }
        container.push(component);

        // if more than one component with same tag are registered, verify they all have an id
        if (container.length > 1) {
            // if a default was already returned, hard-fail here...the developer will need to target their components by ID
            if (this.defaultWasReturned[tagName]) {
                throw new Error(
                    `Cannot register another instance of <${tagName}> because a default was already returned. ` +
                    `Since there are more than one isntance of <${tagName}> in this scope, you will need to either ` +
                    `set the "defaults" property on <mc-scope>, or update every element that references a <${tagName}> ` +
                    `in this scope to specify a "${tagName}-id" attribute. This is enforced to prevent unintended timing issues.`
                );
            }
            for (const containerComponent of container) {
                if (util.isEmptyUnsetOrWhitespace(containerComponent.hostElement.id)) {
                    throw new Error(`Every <${tagName}> component must have an ID when more than one instance is located within an <mc-scope> component`);
                }
            }
        }
        this.register.emit({ tagName, component });
    }

    /**
     * Unregister a component from mc-scope.
     */
    @Input() // use @input to make this method show up on the actual HTML element
    public unregisterComponent = (component: BaseComponent) => {
        const tagName = component.hostElement.tagName;
        let container = this.componentsByTag[tagName.toLowerCase()];
        // fall back to empty array to simplify logic below
        container = container ? container : [];

        // remove this component if registered
        const idx = container.indexOf(component);
        if (idx >= 0) {
            container.splice(idx, 1);
            this.unregister.emit({
                component,
                tagName
            });
        } else {
            const idText = util.isEmptyUnsetOrWhitespace(component.hostElement.id) ? ` with id ${component.hostElement.id}` : '';
            throw new Error(`Cannot unregister <${tagName}>${idText} from <mc-scope> because it was never registered.`);
        }
    }

    /**
     * Get the desired component from scope
     * If ID is present, find the component with that ID or fail if not registered.
     * If ID is NOT present, find the default component with that tag name.
     * If a default ID for a tag is specified, that ID is used. If not,
     * then if a single component of this tag exists, use that. If no default
     * component could be found, an error is thrown.
     *
     * This method will wait for a component to register, or timeout after `maxTimeoutMilliseconds`.
     * There is no polling, but instead it uses eventEmitter subscriptions, so this should resolve as fast
     * as technically possible.
     */
    @Input() // use @input to make this method show up on the actual HTML element
    public getComponent = <TagNameType extends keyof ComponentTagMap>(tagName: TagNameType, id?: string, maxTimeoutMilliseconds = 5000): Promise<ComponentTagMap[TagNameType]> => {
        return new Promise((resolve, reject) => {
            // try to get the component right now
            let result = this._getComponent(tagName, id);
            if (result.component) {
                resolve(result.component);
                return;
            } else if (result.canRetry) {
                let subscription: Subscription;

                // after a timeout, if we still don't have the component, hard-fail
                const removeTimeout = util.timeout(() => {
                    //remove the subscription
                    subscription.unsubscribe();
                    const idText = id ? `, "${id}"` : '';
                    //reject the promise because we have not seen the component we want within the allotted time.
                    reject(`Exceeded maximum wait time of ${maxTimeoutMilliseconds}ms for \`getComponent("${tagName}"${idText}).\`: ${result.error}`);
                }, maxTimeoutMilliseconds);

                // wait for a new component to be registered, and if it maches, great.
                subscription = this.register.subscribe((item: IRegistrationEvent) => {
                    if (item.tagName === tagName) {
                        result = this._getComponent(tagName, id);
                        if (result.component) {
                            // we found a result, use it and clean up
                            resolve(result.component);
                            subscription.unsubscribe();
                            removeTimeout();
                        }
                    }
                });

            }
        });
    }

    /**
     * Internal call to get a component immediately. If the component hasn't registered yet, it will return a null component.
     * If you want to wait for the component to eventually register, you should call `getComponent` (without underscore)
     */
    private _getComponent<TagNameType extends keyof ComponentTagMap>(tagName: TagNameType, id?: string): { component: ComponentTagMap[TagNameType]; error?: string; canRetry: boolean } {
        const targetId = !util.isEmptyUnsetOrWhitespace(id) ? id : this.getAttributeValue(`default-${tagName}-id`);

        let container = this.componentsByTag[tagName.toLowerCase()];
        container = container ? container : [];

        // if we have an ID specified, then find that component or fail
        if (!util.isEmptyUnsetOrWhitespace(targetId)) {
            const targetComponent = container.filter(x => (x as BaseComponent).hostElement.id === targetId)[0];
            if (!targetComponent) {
                return {
                    canRetry: true,
                    component: null,
                    error: `Cannot find a <${tagName}> component with id "${targetId}" registered in this <mc-scope> component`
                };
            } else {
                return {
                    canRetry: false,
                    component: targetComponent as unknown as ComponentTagMap[TagNameType]
                };
            }
        }
        // if we have exactly one of the items registered
        if (container.length === 1) {
            this.defaultWasReturned[tagName] = true;
            return {
                canRetry: true,
                component: container[0] as any
            };
        } else if (container.length === 0) {
            return {
                canRetry: true,
                component: null,
                error: `No <${tagName}> components are registered within <mc-scope>, so no default could be loaded`
            };
        } else {
            return {
                canRetry: false,
                component: null,
                // more than 1 component registered, hard-fail because we couldn't find a default
                error: `Cannot determine the default <${tagName}> component because there are multiple <${tagName}> components registered within this <mc-frame>. Please specify the id of the <${tagName}> component you wish to reference.`
            };
        }
    }
}

export interface IRegistrationEvent {
    tagName: string;
    component: BaseComponent;
}
